//@author: devin
//Migrated by: travis

import { jSith } from "../../../util/jquery-replacement";
import { S25Util } from "../../../util/s25-util";
import { EventFormConflictRepoService } from "./s25.event.form.conflict.repo.service";
import { ObjectSearchAvailabilityUtil } from "../../../util/object.search.availability.util";
import { EventQuotaService } from "../../../services/event.quota.service";
import { EventQuota } from "../../../pojo/EventQuota";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Event } from "../../../pojo/Event";
import { Item } from "../../../pojo/Item";
import { Timeout } from "../../../decorators/timeout.decorator";
import type { EventForm } from "../../../standalone/event-form";

/** The slice of an occurrence the start/end math needs; event form and calendar occurrences both fit. */
export type OccurrenceWindow = Pick<EventForm.Occurrence, "evStartDt" | "evEndDt" | "minutes">;

export class EventFormOccurrenceUtil {
    public static getStartDtFromDtMin(evStartDt: Date, minutes: EventForm.Occurrence.Minutes = {}) {
        return S25Util.date.addMinutes(evStartDt, -1 * ((minutes.setupMinutes || 0) + (minutes.preMinutes || 0)));
    }

    public static getStartDt(occ: OccurrenceWindow) {
        return EventFormOccurrenceUtil.getStartDtFromDtMin(occ.evStartDt, occ.minutes);
    }

    public static getEndDtFromDtMin(evEndDt: Date, minutes: EventForm.Occurrence.Minutes = {}) {
        return S25Util.date.addMinutes(evEndDt, (minutes.postMinutes || 0) + (minutes.takedownMinutes || 0));
    }

    public static getEndDt(occ: OccurrenceWindow) {
        return EventFormOccurrenceUtil.getEndDtFromDtMin(occ.evEndDt, occ.minutes);
    }

    public static getPreDt(occ: Event.Occurrence) {
        return S25Util.date.addMinutes(EventFormOccurrenceUtil.getStartDt(occ), occ.minutes.setupMinutes || 0);
    }

    public static getPostDt(occ: Event.Occurrence) {
        return S25Util.date.addMinutes(occ.evEndDt, occ.minutes.postMinutes || 0);
    }

    public static spansMidnight(occ: Event.Occurrence) {
        return S25Util.date.getDate(occ.evEndDt) > S25Util.date.getDate(occ.evStartDt);
    }

    public static forEachOccOnDate(
        occurrences: any,
        date: Date | null,
        exitEarly: any,
        func: (occ: Event.Occurrence) => any,
        isDatePicker?: boolean,
    ) {
        const compareFn = isDatePicker ? S25Util.date.isBetweenEqual : S25Util.date.isBetween;
        for (let i = 0; i < occurrences.length; i++) {
            const occ = occurrences[i];
            //if null date, add all...
            if (
                !date ||
                compareFn(
                    date,
                    isDatePicker
                        ? S25Util.date.getDate(EventFormOccurrenceUtil.getStartDt(occ))
                        : EventFormOccurrenceUtil.getStartDt(occ),
                    isDatePicker
                        ? S25Util.date.toEndOfDay(EventFormOccurrenceUtil.getEndDt(occ))
                        : EventFormOccurrenceUtil.getEndDt(occ),
                )
            ) {
                const resp = func(occ);
                if (resp && exitEarly) {
                    return resp;
                }
            }
        }
    }

    public static hasOccOnDate(occurrences: any, date: Date | null, isDatePicker?: boolean) {
        return !!EventFormOccurrenceUtil.forEachOccOnDate(
            occurrences,
            date,
            true,
            function () {
                return true;
            },
            isDatePicker,
        );
    }

    public static getOccOnDate(occurrences: any, date: Date | null, isDatePicker?: boolean) {
        const ret: any = [];
        EventFormOccurrenceUtil.forEachOccOnDate(
            occurrences,
            date,
            false,
            function (occ) {
                ret.push(occ);
            },
            isDatePicker,
        );
        ret.sort(S25Util.shallowSortDates("evStartDt", "evEndDt")); //sort by start, then end
        return ret;
    }

    public static getOccObj(
        formModel: Event.Form,
        occ: Event.Occurrence,
        onlyIncluded: any,
        type?: "locations" | "resources",
    ): any {
        //type: "locations", "resources"
        if (!type) {
            return [].concat(
                EventFormOccurrenceUtil.getOccLocations(formModel, occ, onlyIncluded),
                EventFormOccurrenceUtil.getOccResources(formModel, occ, onlyIncluded),
            );
        }
        const objects: any = [];
        jSith.forEach(formModel[type], function (key_, obj) {
            const objOcc = formModel.getObjOcc(obj, occ, true);
            if (objOcc) {
                if ((onlyIncluded && objOcc.isIncluded) || !onlyIncluded) {
                    objects.push(obj);
                }
            }
        });
        return objects;
    }

    public static getOccLocations(formModel: Event.Form, occ: Event.Occurrence, onlyIncluded: any) {
        return EventFormOccurrenceUtil.getOccObj(formModel, occ, onlyIncluded, "locations");
    }

    public static getOccResources(formModel: Event.Form, occ: Event.Occurrence, onlyIncluded: any) {
        return EventFormOccurrenceUtil.getOccObj(formModel, occ, onlyIncluded, "resources");
    }

    public static getAllObjOcc(formModel: Event.Form, onlyIncluded: any, type?: "locations" | "resources") {
        let all: any = [];
        jSith.forEach(formModel.occurrences, function (key, occ) {
            all = all.concat(EventFormOccurrenceUtil.getOccObj(formModel, occ, onlyIncluded, type));
        });
        return all;
    }

    public static getAllLocationOcc(formModel: Event.Form, onlyIncluded: any) {
        return EventFormOccurrenceUtil.getAllObjOcc(formModel, onlyIncluded, "locations");
    }

    public static getAllResourceOcc(formModel: Event.Form, onlyIncluded: any) {
        return EventFormOccurrenceUtil.getAllObjOcc(formModel, onlyIncluded, "resources");
    }

    public static getOccMissingObj(formModel: Event.Form, type: "locations" | "resources") {
        //type: locations | resources
        const occs: any = [];
        jSith.forEach(formModel.occurrences, function (key, occ) {
            if (
                occ.state &&
                [99, 2].indexOf(occ.state.itemId) === -1 &&
                EventFormOccurrenceUtil.getOccObj(formModel, occ, true, type).length === 0
            ) {
                occs.push(occ);
            }
        });
        return occs;
    }

    @Timeout
    public static async checkQuotas(formModel: Event.Form, locations?: S25ItemI[]): Promise<EventQuota.CheckResult[]> {
        let retVal: EventQuota.CheckResult[] | undefined;
        //Start by removing any existing quotas.
        S25Util.propertyDelete(formModel.occurrences, "hasQuotaConflict");
        S25Util.propertyDelete(formModel.occurrences, "quotaConflict");

        const primaryOrgId: number | undefined = formModel?.itemDict?.primaryOrg?.data?.itemId;
        const eventTypeId: number | undefined = formModel?.itemDict?.type?.data?.itemId;

        const activeOccurrences: Event.Occurrence[] = S25Util.array
            .forceArray(formModel.occurrences)
            .filter((occ: Event.Occurrence) => {
                return occ?.state?.itemId === 1;
            });
        const state: number = formModel?.itemDict?.state?.data;
        let processedLocations: EventQuota.LocationItem[];

        if (S25Util.isDefined(locations) && locations.length > 0) {
            processedLocations = S25Util.array
                .forceArray(locations)
                .filter((loc: S25ItemI) => loc.itemTypeId === 4)
                .map((id: S25ItemI): EventQuota.LocationItem => {
                    return {
                        itemId: id.itemId,
                        itemName: id.itemName,
                        itemTypeId: 4,
                    };
                });
        } else {
            processedLocations =
                formModel
                    .getLocOccs()
                    .filter((occ: Event.Occurrence) => occ.isIncluded)
                    .map((obj: Event.Occurrence): EventQuota.LocationItem => {
                        return {
                            itemId: obj.item.itemId,
                            occ_id: obj.occ.uuid,
                            itemName: obj.item.itemName,
                            itemTypeId: 4,
                        };
                    }) || [];
        }

        if (![0, 98, 99].includes(state)) {
            const eventId = formModel?.eventData?.event_id ? S25Util.parseInt(formModel.eventData.event_id) : undefined;
            const eventStartDt = formModel?.timeModel?.evStartDt;
            const eventEndDt = formModel?.timeModel?.evEndDt;
            const eventContext =
                eventId || eventStartDt || eventEndDt ? { eventId, eventStartDt, eventEndDt } : undefined;

            const quota: EventQuota.CheckResult[] = await EventQuotaService.check(
                activeOccurrences,
                processedLocations,
                primaryOrgId,
                eventTypeId,
                eventContext,
            );
            if (quota?.length > 0) {
                quota.forEach((q: EventQuota.CheckResult) => {
                    const occ: Event.Occurrence = S25Util.array.getByProp(activeOccurrences, "uuid", q.occ.uuid);
                    if (q.occ && !!q.occ.object_id) {
                        const loc: S25ItemI = S25Util.array.getByProp(formModel.locations, "itemId", q.quota.quota_id);
                        q.occ.location = loc;
                        if (loc && loc.checkedObj) loc.checkedObj.hasQuotaConflict = true;
                    }

                    if (occ) {
                        occ.hasQuotaConflict = true;
                        occ.quotaConflict = q;
                    }
                });

                retVal = quota;
            }
        }
        return jSith.when(retVal || []);
    }

    public static checkAvailability(
        formModel: EventForm,
        items: any,
        perItemFunc?: (item: any) => any,
        specificOccs?: any,
    ) {
        const thisPerItemFunc = function (item: any) {
            (perItemFunc || jSith.noop)(item);

            if (specificOccs?.length) {
                jSith.forEach(specificOccs, function (key, occ) {
                    EventFormConflictRepoService.removeConflictByObjOccModel(formModel, item, occ);
                });
            } else {
                EventFormConflictRepoService.removeConflictByObjectModel(formModel, item);
            }

            const formItems = (item.itemTypeId === Item.Ids.Location ? formModel.locations : formModel.resources) ?? [];
            const formHasItem = formItems.some((formItem) => formItem.itemId === item.itemId);
            if (!formHasItem) return;

            if (item.checkedObj.hasRealConflicts) {
                jSith.forEach(item.checkedObj.conflicts, function (key, conflict) {
                    conflict.isReal && EventFormConflictRepoService.addConflictByModel(formModel, conflict);
                });
            }
        };

        const reservations: any = [];
        jSith.forEach(specificOccs?.length ? specificOccs : formModel.occurrences, function (key, occ) {
            if ([99, 2].indexOf(occ.state.itemId) === -1) {
                //no cancelled or excluded
                const rsrv: any = {
                    occUUID: occ.uuid,
                    reservation_start_dt: EventFormOccurrenceUtil.getStartDt(occ),
                    reservation_end_dt: EventFormOccurrenceUtil.getEndDt(occ),
                    space_reservation: [],
                    resource_reservation: [],
                };

                reservations.push(rsrv);

                jSith.forEach(items, function (key, item) {
                    const objOcc = formModel.getObjOcc(item, occ, true);

                    if (objOcc) {
                        if (objOcc.isIncluded && item.itemTypeId === 4) {
                            rsrv.space_reservation.push({
                                space_id: item.itemId,
                                share: objOcc.spaceShare ? "T" : "F",
                                always_shared: item.always_shared,
                            });
                        } else if (item.itemTypeId === 6) {
                            //note: we still call avail for UNincluded resources to get quantity available
                            rsrv.resource_reservation.push({
                                resource_id: item.itemId,
                                quantity: objOcc.resourceQuantity,
                                isIncluded: objOcc.isIncluded,
                            });
                        }
                    }
                });
            }
        });

        return ObjectSearchAvailabilityUtil.checkAvailability(
            formModel.eventId,
            formModel.profileId,
            reservations,
            items,
            thisPerItemFunc,
            specificOccs,
        );
    }

    /** Stamps {@link getExceedingResourceQuantity} onto `resource.checkedObj`, where the occurrence rows read it. */
    public static checkExceedingResourceQuantity(
        resource: { itemId: number; checkedObj?: EventForm.ObjectOccurrenceCheckedObj },
        occurrences: EventForm.ObjectOccurrence[],
    ): void {
        resource.checkedObj ??= {};
        resource.checkedObj.exceedingQuantity = EventFormOccurrenceUtil.getExceedingResourceQuantity(
            resource,
            occurrences,
        );
    }

    /** Time windows where overlapping occurrences of `resource` reserve more than its max quantity. */
    public static getExceedingResourceQuantity(
        resource: { itemId: number },
        occurrences: EventForm.ObjectOccurrence[],
    ): EventForm.ObjectOccurrenceExceedingQuantity[] {
        const exceedingQuantity: EventForm.ObjectOccurrenceExceedingQuantity[] = [];

        const occs = occurrences
            .filter((objOcc) => objOcc.item.itemTypeId === Item.Ids.Resource && objOcc.item.itemId === resource.itemId)
            .sort((left, right) => left.occ.evStartDt.getTime() - right.occ.evStartDt.getTime());

        for (let i = 0; i < occs.length; i++) {
            const objOcc = occs[i];
            const start = EventFormOccurrenceUtil.getStartDtFromDtMin(objOcc.occ.evStartDt, objOcc.occ.minutes || {});
            const end = EventFormOccurrenceUtil.getEndDtFromDtMin(objOcc.occ.evEndDt, objOcc.occ.minutes || {});
            let reserved = Number(objOcc.resourceQuantity) || 0;
            const overlapping: EventForm.ObjectOccurrence[] = [objOcc];
            for (let j = i + 1; j < occs.length; j++) {
                const laterOcc = occs[j];
                const laterStart = EventFormOccurrenceUtil.getStartDtFromDtMin(
                    laterOcc.occ.evStartDt,
                    laterOcc.occ.minutes || {},
                );
                if (laterStart < end) {
                    reserved += Number(laterOcc.resourceQuantity) || 0;
                    overlapping.push(laterOcc);
                } else break;
            }
            if (objOcc.maxQuantity != null && reserved > objOcc.maxQuantity) {
                exceedingQuantity.push({
                    start,
                    end,
                    occIds: new Set(overlapping.map((overlapOcc) => overlapOcc.occ.uuid)),
                    occs: overlapping,
                    reserved,
                    max: objOcc.maxQuantity,
                });
            }
        }

        return exceedingQuantity;
    }

    /**
     * Real event profiles ("Reservation" use) carry `prof_use === 1`. Some events also store
     * setup/takedown/constraint profiles under other `prof_use` values that must never be treated
     * as the savable event profile. A profile with no `prof_use` is treated as real (matches the
     * event-edit hydrate factory semantics).
     */
    public static isRealEventProfile(profile: any): boolean {
        if (!profile) {
            return false;
        }
        const profUse = profile.prof_use;
        return profUse == null || parseInt(profUse) === 1;
    }

    /**
     * Resolves the single real event profile (prof_use null/1) from a profile list/node,
     * ignoring setup/takedown/constraint profiles. Returns null when there is not exactly one
     * real profile so callers can fall back or reject rather than guessing which to save.
     */
    public static getSoleRealEventProfile(profiles: any): any {
        const realProfiles = S25Util.array
            .forceArray(profiles ?? [])
            .filter((profile: any) => EventFormOccurrenceUtil.isRealEventProfile(profile));
        return realProfiles.length === 1 ? realProfiles[0] : null;
    }

    public static mergePrefOccsIntoProfile(
        eventData: any,
        profile: any,
        prefFlag?: any,
        onSpMergeF?: any,
        onAllEventStates?: any,
        newObjOccStatus?: string,
    ) {
        if (!profile) {
            return [];
        }
        newObjOccStatus = newObjOccStatus || "new";
        const eventText = eventData.event_text;
        const rsrvPrefs = S25Util.propertyGetParentWithChildValue(eventText, "text_type_id", 9);
        const prefJson = rsrvPrefs && rsrvPrefs.text && S25Util.parseSpaceResourcePreferences(rsrvPrefs.text);

        //if event is draft, add loc/res preferences as real ones again (ANG-1751)
        if (prefJson && (parseInt(eventData.state) === 0 || onAllEventStates)) {
            if (prefJson && prefJson.reservation_list) {
                jSith.forEach(prefJson.reservation_list.reservation, function (key, prefRsrv) {
                    const rsrv = S25Util.propertyGetParentWithChildValue(
                        profile.reservation,
                        "reservation_id",
                        prefRsrv.reservation_id,
                    );
                    if (rsrv) {
                        if (!rsrv.space_reservation) {
                            rsrv.space_reservation = [];
                        }

                        if (!rsrv.resource_reservation) {
                            rsrv.resource_reservation = [];
                        }

                        jSith.forEach(prefRsrv.space_reservation, function (key, spRsrv) {
                            if (
                                spRsrv.space_id > 0 &&
                                !S25Util.valueFind(rsrv.space_reservation, "space_id", spRsrv.space_id)
                            ) {
                                onSpMergeF && onSpMergeF();

                                const newSpRsrv: any = {
                                    status: newObjOccStatus,
                                    space_id: spRsrv.space_id,
                                    space_instructions: spRsrv.space_instructions,
                                    share: spRsrv.share,
                                    attendance: spRsrv.attendance,
                                    layout_id: spRsrv.layout_id,
                                    layout_name: null,
                                    space: {
                                        space_name: spRsrv.space_name + "",
                                        formal_name: null,
                                        max_capacity: null,
                                    },
                                };

                                if (prefFlag) {
                                    newSpRsrv.isPreference = true;
                                }

                                if (newObjOccStatus !== "est" && profile.status === "est") {
                                    profile.status = "mod";
                                }
                                rsrv.space_reservation.push(newSpRsrv);
                            }
                        });

                        jSith.forEach(prefRsrv.resource_reservation, function (key, rsRsrv) {
                            if (
                                rsRsrv.resource_id > 0 &&
                                !S25Util.valueFind(rsrv.resource_reservation, "resource_id", rsRsrv.resource_id)
                            ) {
                                const newRsRsrv: any = {
                                    status: newObjOccStatus,
                                    resource_id: rsRsrv.resource_id,
                                    quantity: rsRsrv.quantity,
                                    resource_instructions: rsRsrv.resource_instructions,
                                    resource: {
                                        resource_name: rsRsrv.resource_name + "",
                                    },
                                };

                                if (prefFlag) {
                                    newRsRsrv.isPreference = true;
                                }

                                if (newObjOccStatus !== "est" && profile.status === "est") {
                                    profile.status = "mod";
                                }
                                rsrv.resource_reservation.push(newRsRsrv);
                            }
                        });
                    }
                });
            }
        }

        return (prefJson && prefJson.reservation_list && prefJson.reservation_list.reservation) || [];
    }
}
